import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DownloadSimple, UploadSimple, CheckCircle, WarningCircle, ArrowLeft, FileCsv } from "@phosphor-icons/react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function BulkUpload() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);

  const isVendor = user?.is_vendor || user?.role === "admin";
  const hasPaymentPath = user?.paypal_link || user?.cashapp_handle || user?.venmo_link;

  if (!isVendor) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="bulk-vendor-gate">
        <div className="font-mono text-xs text-[#FFD700] tracking-widest uppercase">→ Vendor Account Required</div>
        <h1 className="mt-3 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">Bulk Upload Listings</h1>
        <p className="mt-6 text-zinc-400">Vendor accounts unlock bulk CSV uploads. Upgrade in your Account to continue.</p>
        <Link to="/account?upgrade=1" data-testid="bulk-upgrade-cta" className="h57-btn-primary inline-block mt-6">Upgrade to Vendor →</Link>
      </div>
    );
  }

  if (!hasPaymentPath) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <h1 className="font-display font-black uppercase text-3xl">Add a Payment Path First</h1>
        <p className="mt-4 text-zinc-400">Add at least one payment path (PayPal, Cash App, or Venmo) to your Account before bulk-uploading.</p>
        <Link to="/account" className="h57-btn-primary inline-block mt-6">Go to Account →</Link>
      </div>
    );
  }

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/listings/bulk/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "hunt57-listings-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setDownloading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/listings/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.errors?.length === 0) {
        toast.success(`Uploaded ${data.created.length} listing${data.created.length === 1 ? "" : "s"}`);
      } else {
        toast(`${data.created.length} created · ${data.errors.length} errors`, { icon: "⚠" });
      }
    } catch (err) { toast.error(formatErr(err)); }
    finally { setUploading(false); }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 md:py-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        data-testid="bulk-back"
        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white mb-3"
      >
        <ArrowLeft size={12} weight="bold" /> Back
      </button>
      <div className="font-mono text-xs text-[#FF5F1F] tracking-widest uppercase">→ Bulk Upload</div>
      <h1 className="mt-2 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">Bulk Upload Listings</h1>
      <p className="mt-4 text-zinc-400 leading-relaxed max-w-2xl">
        Post many cards in one shot. Download the template, fill one row per card, and upload the file.
        Rows with errors are skipped and reported — valid rows in the same file still get created.
      </p>
      <p className="mt-2 font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
        → Images are added after upload via the Edit button on each listing.
      </p>
      <div className="mt-3">
        <Link
          to="/sell/bulk/photos"
          data-testid="bulk-psa-link"
          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#FFD700] hover:text-white"
        >
          → Have graded slabs? Bulk PSA photo match by folder
        </Link>
      </div>

      {/* Step 1 — Download */}
      <section className="mt-8 h57-surface p-6" data-testid="bulk-step-download">
        <div className="flex items-start gap-4">
          <div className="font-display font-black text-3xl text-[#FF5F1F] tracking-tighter leading-none">01</div>
          <div className="flex-1">
            <h2 className="font-display font-black uppercase text-lg tracking-tight">Download the Template</h2>
            <p className="mt-2 text-sm text-zinc-400">
              A CSV with one row of sample data. Open it in Excel, Google Sheets, or Numbers, then replace the sample row with your cards.
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <button
                onClick={downloadTemplate}
                disabled={downloading}
                data-testid="bulk-download-template"
                className="h57-btn-primary inline-flex items-center justify-center gap-2"
              >
                <DownloadSimple size={16} weight="bold" />
                {downloading ? "Preparing…" : "Download CSV Template"}
              </button>
              <div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase self-center">
                → 29 columns · UTF-8 · comma-separated
              </div>
            </div>
            <details className="mt-5 border-t border-white/10 pt-4">
              <summary className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 cursor-pointer hover:text-white">
                Column reference (click to expand)
              </summary>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 font-mono text-[11px] text-zinc-400">
                {[
                  "sport *", "player_name *", "year",
                  "brand", "set_name", "team",
                  "card_number", "parallel", "variation_note",
                  "base", "insert", "variation",
                  "rookie", "autograph", "relic",
                  "relic_type", "numbered", "serial_number",
                  "ssp", "sp", "graded",
                  "grading_company", "grade", "cert_number",
                  "condition", "price *", "shipping_price",
                  "accepts_offers", "notes",
                ].map((c) => (
                  <div key={c}>{c}</div>
                ))}
              </div>
              <div className="mt-4 text-[11px] text-zinc-500 leading-relaxed space-y-1">
                <p><span className="text-white">*</span> required. Everything else is optional.</p>
                <p><span className="text-zinc-400 font-mono">Booleans:</span> true / false / yes / no / 1 / 0 / blank (blank = false)</p>
                <p><span className="text-zinc-400 font-mono">relic_type:</span> player_worn · game_used · blank</p>
                <p><span className="text-zinc-400 font-mono">condition:</span> free text (Near Mint, Ungraded, etc.)</p>
                <p><span className="text-zinc-400 font-mono">price / shipping_price:</span> numeric (no $ sign)</p>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Step 2 — Upload */}
      <section className="mt-6 h57-surface p-6" data-testid="bulk-step-upload">
        <div className="flex items-start gap-4">
          <div className="font-display font-black text-3xl text-[#FF5F1F] tracking-tighter leading-none">02</div>
          <div className="flex-1">
            <h2 className="font-display font-black uppercase text-lg tracking-tight">Upload Your CSV</h2>
            <p className="mt-2 text-sm text-zinc-400">
              Fill in the template, save as CSV, then drop or select the file here.
            </p>
            <form onSubmit={submit} className="mt-4 space-y-3">
              <label
                htmlFor="bulk-file"
                className={`flex flex-col items-center justify-center border-2 border-dashed transition-colors cursor-pointer py-10 px-4 text-center ${
                  file ? "border-[#FF5F1F] bg-[#FF5F1F]/5" : "border-white/15 hover:border-white/30"
                }`}
                data-testid="bulk-file-drop"
              >
                {file ? (
                  <>
                    <FileCsv size={32} weight="bold" className="text-[#FF5F1F]" />
                    <div className="mt-2 font-mono text-xs uppercase tracking-widest">{file.name}</div>
                    <div className="mt-1 font-mono text-[10px] text-zinc-500">{(file.size / 1024).toFixed(1)} KB</div>
                  </>
                ) : (
                  <>
                    <UploadSimple size={32} weight="bold" className="text-zinc-500" />
                    <div className="mt-2 font-mono text-xs uppercase tracking-widest text-zinc-400">Click to choose a CSV</div>
                    <div className="mt-1 font-mono text-[10px] text-zinc-600">.csv · UTF-8 · up to a few MB</div>
                  </>
                )}
                <input
                  id="bulk-file"
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  data-testid="bulk-file-input"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={!file || uploading}
                  data-testid="bulk-submit"
                  className="h57-btn-primary flex-1 inline-flex items-center justify-center gap-2"
                >
                  <UploadSimple size={16} weight="bold" />
                  {uploading ? "Uploading…" : "Upload & Create Listings"}
                </button>
                {file && (
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="bulk-reset"
                    className="h57-btn-secondary text-sm"
                  >
                    Clear
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Step 3 — Result */}
      {result && (
        <section className="mt-6 h57-surface p-6" data-testid="bulk-result">
          <div className="flex items-start gap-4">
            <div className="font-display font-black text-3xl text-[#FF5F1F] tracking-tighter leading-none">03</div>
            <div className="flex-1">
              <h2 className="font-display font-black uppercase text-lg tracking-tight">Result</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="h57-chip h57-chip-orange" data-testid="bulk-result-created">
                  <CheckCircle size={12} weight="fill" /> {result.created.length} created
                </span>
                {result.errors.length > 0 && (
                  <span className="h57-chip !border-red-500/50 !text-red-400" data-testid="bulk-result-errors">
                    <WarningCircle size={12} weight="fill" /> {result.errors.length} error{result.errors.length === 1 ? "" : "s"}
                  </span>
                )}
                <span className="h57-chip">{result.total_rows} row{result.total_rows === 1 ? "" : "s"} total</span>
              </div>

              {result.created.length > 0 && (
                <div className="mt-5">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">Created listings</div>
                  <ul className="space-y-1 text-sm">
                    {result.created.map((l) => (
                      <li key={l.id} className="flex items-center justify-between gap-2 border-b border-white/5 pb-1">
                        <span className="truncate text-zinc-300">{l.title}</span>
                        <Link
                          to={`/listing/${l.id}/edit`}
                          data-testid={`bulk-created-edit-${l.id}`}
                          className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F] hover:underline shrink-0"
                        >
                          Add photo →
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.errors.length > 0 && (
                <div className="mt-5">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-red-400 mb-2">Row errors</div>
                  <ul className="space-y-1 text-xs">
                    {result.errors.map((e, i) => (
                      <li key={i} className="flex gap-3 border-b border-white/5 pb-1">
                        <span className="font-mono text-red-400 shrink-0">row {e.row}</span>
                        <span className="text-zinc-400 break-words">{e.message}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 text-[11px] text-zinc-500">
                    Fix the rows above in your source CSV and re-upload — only the failed rows need to be included on the next pass.
                  </p>
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                <Link to="/my-listings" data-testid="bulk-goto-mylistings" className="h57-btn-primary text-sm">
                  Go to My Listings →
                </Link>
                <button onClick={reset} className="h57-btn-secondary text-sm" data-testid="bulk-upload-more">
                  Upload another CSV
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
